"use client";

import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";

import {
  fetchCurrentUser,
  TransientSessionError,
  UnauthorizedSessionError,
} from "@/lib/session";
import type { SessionContextValue, SessionState } from "@/hooks/useSession";
import { SessionContext } from "@/hooks/useSession";

interface SessionProviderProps {
  children: ReactNode;
}

const SESSION_CACHE_KEY = "gt_session_cache";
const ACCESS_TOKEN_STORAGE_KEY = "auth-token";
const PUBLIC_AUTH_ROUTE_PREFIXES = [
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/reset-password",
  "/signup",
];

const createEmptySession = (): SessionState => ({
  userId: "",
  tenantId: undefined,
  role: undefined,
  permissions: [],
  accountId: undefined,
});

function decodeBase64Url(value: string): string {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  return atob(padded);
}

function readJwtSub(token: string): string | undefined {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return undefined;
    const json = decodeBase64Url(parts[1] ?? "");
    const payload = JSON.parse(json) as { sub?: string };
    return typeof payload.sub === "string" && payload.sub ? payload.sub : undefined;
  } catch {
    return undefined;
  }
}

function readLocalAccessTokenSub(): string | undefined {
  try {
    const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    if (!token) return undefined;
    return readJwtSub(token);
  } catch {
    return undefined;
  }
}

function loadCachedSession(): SessionState {
  try {
    const raw = localStorage.getItem(SESSION_CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as SessionState;
      if (parsed && parsed.userId) {
        const tokenSub = readLocalAccessTokenSub();
        // If we have a local token and it belongs to a different user, ignore stale cache.
        if (tokenSub && tokenSub !== parsed.userId) {
          try {
            localStorage.removeItem(SESSION_CACHE_KEY);
          } catch {
            // ignore
          }
          return createEmptySession();
        }
        return parsed;
      }
    }
  } catch {
    // localStorage unavailable or corrupted
  }
  return createEmptySession();
}

function persistSessionCache(session: SessionState) {
  try {
    if (session.userId) {
      localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(SESSION_CACHE_KEY);
    }
  } catch {
    // localStorage unavailable
  }
}

function clearStoredAuthState() {
  try {
    localStorage.removeItem(SESSION_CACHE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  } catch {
    // localStorage unavailable
  }
}

export function clearSessionCache() {
  clearStoredAuthState();
}

export function SessionProvider({ children }: SessionProviderProps) {
  const pathname = usePathname();
  const mountedRef = useRef(true);
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const lastRefreshStartedAtRef = useRef(0);
  const MIN_REFRESH_INTERVAL_MS = 2_000;
  // Start with empty session to avoid server/client hydration mismatch (localStorage is undefined on server).
  // Hydrate from cache in useEffect so first paint matches server output.
  const [session, setSession] = useState<SessionState>(createEmptySession);
  const [status, setStatus] = useState<SessionContextValue["status"]>("idle");
  const [error, setError] = useState<string | undefined>();
  const shouldSkipInitialRefresh = useMemo(() => {
    const currentPath = (pathname ?? "").toLowerCase();
    if (!PUBLIC_AUTH_ROUTE_PREFIXES.some((prefix) => currentPath.startsWith(prefix))) {
      return false;
    }
    try {
      // Skip eager /api/auth/me probes on public auth routes when no local token exists.
      // This removes noisy expected 401s and keeps the login page snappy.
      const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
      return !token;
    } catch {
      return true;
    }
  }, [pathname]);

  const refreshSession = useCallback(
    async (options?: { force?: boolean }): Promise<boolean> => {
      if (!mountedRef.current) return false;
      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }

      const force = options?.force === true;
      const now = Date.now();
      if (!force && now - lastRefreshStartedAtRef.current < MIN_REFRESH_INTERVAL_MS) {
        return false;
      }
      lastRefreshStartedAtRef.current = now;

      const refreshPromise = (async (): Promise<boolean> => {
        if (!mountedRef.current) return false;
        // If the app just switched identities (e.g. sign-in to another account),
        // ensure we don't keep showing a cached user on transient refresh failures.
        const expectedUserId = readLocalAccessTokenSub();
        if (expectedUserId) {
          setSession((current) => {
            if (current.userId && current.userId !== expectedUserId) {
              const empty = createEmptySession();
              persistSessionCache(empty);
              return empty;
            }
            return current;
          });
        }
        setStatus("loading");
        try {
          const next = await fetchCurrentUser();
          if (!mountedRef.current) return false;
          setSession(next);
          persistSessionCache(next);
          setStatus("ready");
          setError(undefined);
          return true;
        } catch (err) {
          if (!mountedRef.current) return false;
          if (err instanceof UnauthorizedSessionError) {
            setSession(createEmptySession());
            clearStoredAuthState();
            setStatus("idle");
            setError(undefined);
            return false;
          }
          if (err instanceof TransientSessionError) {
            setSession((current) => {
              if (current.userId) return current;
              return createEmptySession();
            });
            setStatus("error");
            setError(err.message);
            return false;
          }
          // On network errors, keep cached data if available
          setSession((current) => {
            if (current.userId) return current; // keep cached
            return createEmptySession();
          });
          setStatus("error");
          setError(err instanceof Error ? err.message : "Failed to load session");
          return false;
        }
      })();

      try {
        refreshInFlightRef.current = refreshPromise;
        return await refreshPromise;
      } finally {
        if (refreshInFlightRef.current === refreshPromise) {
          refreshInFlightRef.current = null;
        }
      }
    },
    [],
  );

  // Hydrate from localStorage cache on mount so UI updates quickly (avoids flash of empty state).
  useEffect(() => {
    try {
      const cached = loadCachedSession();
      if (cached?.userId) {
        setSession(cached);
        setStatus("ready");
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (shouldSkipInitialRefresh) {
      return;
    }
    void refreshSession();
  }, [refreshSession, shouldSkipInitialRefresh]);

  // Cross-tab sync: when auth-token or session cache changes in another tab, refresh.
  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.key !== ACCESS_TOKEN_STORAGE_KEY && event.key !== SESSION_CACHE_KEY) return;
      if (!mountedRef.current) return;

      if (event.key === ACCESS_TOKEN_STORAGE_KEY && !event.newValue) {
        const empty = createEmptySession();
        setSession(empty);
        persistSessionCache(empty);
        setStatus("idle");
        setError(undefined);
        return;
      }

      void refreshSession();
    };

    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [refreshSession]);

  // Auto-retry session load when the backend recovers from an outage.
  // Listens for a lightweight heartbeat to avoid hammering a dead server.
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (status !== "error") {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      return;
    }

    const attemptRetry = async () => {
      try {
        const res = await fetch("/api/heartbeat", { cache: "no-store" });
        if (res.ok) {
          const body = await res.json();
          if (body.status === "ok" || body.status === "degraded") {
            // Backend is back -- refresh session
            void refreshSession();
            return;
          }
        }
      } catch {
        // still down
      }
      // Schedule next retry with backoff (10s)
      if (mountedRef.current) {
        retryTimerRef.current = setTimeout(attemptRetry, 10_000);
      }
    };

    retryTimerRef.current = setTimeout(attemptRetry, 5_000);
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [status, refreshSession]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      ...session,
      status,
      error,
      updateSession: (next: Partial<SessionState>) => {
        setSession((current) => {
          const updated = { ...current, ...next };
          persistSessionCache(updated);
          return updated;
        });
      },
      setStatus: (nextStatus: SessionContextValue["status"], nextError?: string) => {
        setStatus(nextStatus);
        setError(nextError);
      },
      refreshSession,
    }),
    [session, status, error, refreshSession],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
